import Big from 'big.js'

// Number of decimal places to round to in calculations
export const DECIMAL_PLACES = 2

const COMMA_DECIMAL_REGEX = /^-?(\d{1,3}(\.\d{3})*|\d+),\d{2}$/
const US_THOUSANDS_COMMA_REGEX = /^-?(\d{1,3}(,\d{3})*)\.\d{2}$/

export function isValidInputDecimal(value: string): boolean {
  const relaxedDecimalRegex = new RegExp(
    `^-?\\d*(?:\\.\\d{0,${DECIMAL_PLACES}})?$`
  )

  if (['', '-'].includes(value)) return true

  return relaxedDecimalRegex.test(value)
}

/**
 * Checks if a string represents a valid number with exactly two decimal places.
 * Accepts both positive and negative numbers.
 *
 * Examples:
 *  "12.34"   → valid
 *  "-12.34"  → valid
 *  "0.00"    → valid
 *  "-0.01"   → valid
 *  "12.345"  → invalid
 *  "abc"     → invalid
 */
export function isValidTwoDecimalString(value: string): boolean {
  const decimalRegex = new RegExp(`^-?\\d+[.,]\\d{${DECIMAL_PLACES}}$`)

  return decimalRegex.test(value)
}

/**
 * Converts a string with exactly 2 decimal places to a Big.js instance.
 * Throws an error if the string format is invalid.
 *
 * @param value - The string to convert.
 * @returns Big instance
 */
export function convertTwoDecimalStringToBig(value: string): Big {
  const sanitizedValue = sanitizeCommaDecimal(value)

  if (!isValidTwoDecimalString(sanitizedValue)) {
    throw new Error(
      `Invalid format: "${value}". Must be a decimal with 2 digits after the point.`
    )
  }

  return Big(sanitizedValue)
}

/**
 * Formats a Big.js number as a string with exactly 2 decimal places.
 * Trailing zeros are preserved.
 *
 * Examples:
 *   123    -> "123.00"
 *   123.4  -> "123.40"
 *   123.45 -> "123.45"
 *   0      -> "0.00"
 *
 * @param value - Big.js instance
 * @returns String with exactly two decimal places
 */
export function formatBigFixedTwoDecimals(value: Big): string {
  return value.toFixed(DECIMAL_PLACES) // Always returns a string with 2 decimals
}

/**
 * Returns a Big instance representing zero.
 */
export function getBigZero(): Big {
  return Big(0)
}

/**
 * Safely converts a Big.js instance to a native JavaScript number,
 * rounding to two decimal places using half-up rounding.
 *
 * This function performs a round-trip precision check to ensure that converting
 * from Big to Number does not introduce rounding errors or precision loss.
 *
 * @param bigValue - A Big.js instance representing a precise decimal value.
 * @returns The native number rounded to two decimal places.
 * @throws Error if precision loss is detected during conversion.
 */
export function safeBigToNumber(bigValue: Big): number {
  const stringValue = bigValue.toFixed(DECIMAL_PLACES)
  const numberValue = bigValue.round(DECIMAL_PLACES, Big.roundHalfUp).toNumber()
  const roundTrip = new Big(numberValue).toFixed(DECIMAL_PLACES)

  if (stringValue !== roundTrip) {
    throw new Error(`Precision loss detected: ${stringValue} → ${roundTrip}`)
  }
  return numberValue
}
/**
 * Returns a string or number depending on the type of the default value
 *
 * @param value
 * @param defaultValue
 * @returns
 */
export function normalizeInput(
  value: string | number | null | undefined,
  defaultValue: string | number = ''
): string | number {
  const isEmpty = value === undefined || value === null || value === ''

  if (isEmpty) return defaultValue

  const bigValue = Big(value)

  return typeof defaultValue === 'string'
    ? formatBigFixedTwoDecimals(bigValue)
    : bigValue.round(2, Big.roundUp).toNumber()
}

/**
 * Converts a locale-specific decimal string (e.g. Colombian format "1.175,00")
 * to a standard dot-decimal format ("1175.00") for safe parsing and storage.
 *
 * Only applies transformation if the input matches the expected comma-decimal format.
 *
 * @param value - The raw input string in locale-specific format
 * @returns A sanitized decimal string with dot as decimal separator, or the original value if format is invalid
 */
export function sanitizeCommaDecimal(value: string): string {
  if (COMMA_DECIMAL_REGEX.test(value)) {
    // Colombian format: "2.181,00" → "2181.00"
    return value.replace(/\./g, '').replace(',', '.')
  }
  if (US_THOUSANDS_COMMA_REGEX.test(value)) {
    // US format: "2,181.00" → "2181.00"
    return value.replace(/,/g, '')
  }
  return value
}
